import { defineStore } from "pinia";
import { computed, ComputedRef, Ref, ref, watch } from "vue";

import { steps as generateSteps } from "@/utils/checkout";
import { useEventsStore } from "./events";
import { useLoader } from "./loader";
import getTicketsBySector from "@/api/requests/getTicketsBySector";
import Event from "@/types/Event";
import Sector from "@/types/Sector";
import Section from "@/types/Section";
import { useSnackbar } from "./snackbar";
import NominalTicket from "@/types/NominalTicket";
import Fee from "@/utils/fees";
import { useStepper } from "@/composables/useStepper";
import { useCoupons } from "@/composables/useCoupons";
import TicketType from "@/types/TicketType";
import { useCheckoutStore } from "./checkout";

function activeBatchPrice(type: TicketType): number {
  return type.current_batch?.price ?? 0;
}

export const useCartStore = defineStore("cart", () => {
  const loader = useLoader();

  const snackbar = useSnackbar();

  const events = useEventsStore();

  const sectors: Ref<Sector[]> = ref([]);

  const coupons = useCoupons(sectors);

  const checkout = useCheckoutStore();

  const tickets: Ref<NominalTicket[]> = ref([]);

  const steps = computed(() => {
    const event = events.selected;

    if (event === null) {
      return [];
    }

    const _steps = generateSteps(event, sectors.value, tickets.value, checkout.order);

    return _steps.filter(s => s.condition());
  });

  const stepper = useStepper(steps);

  /**
   * Returns a flattened list of all ticket types in the cart, created from the base sectors.
   *
   * Read-only list made to simplify other computations.
   */
  const types: ComputedRef<TicketType[]> = computed(() => sectors.value.flatMap(sector => sector.sections.flatMap(section => section.tickets)));

  /**
   * Returns the total number of selected tickets in the cart.
   */
  const totalItems = computed(() => types.value.reduce((acc, type) => acc + type.quantidade, 0));

  /**
   * Returns the sum of prices for all selected tickets, multiplying by their quantities.
   *
   * Does not include fees or coupon application.
   */
  const totalPrice: ComputedRef<number> = computed(
    () => types.value.reduce(
      (acc, type) => acc + activeBatchPrice(type) * type.quantidade, 0,
    ),
  );

  /**
   * Returns the total fees for all selected tickets, multiplying by their quantities.
   *
   * Does not include ticket prices or coupon application.
   */
  const totalFees: ComputedRef<number> = computed(
    () => types.value.reduce(
      (acc, type) => {
        const fee = Fee.calculate(
          activeBatchPrice(type),
          events.selected!.convenience_fee,
          events.selected!.convenience_fee_mode,
        );

        return acc + fee * type.quantidade;
      },
      0,
    ),
  );

  /**
   * Returns the total amount of the cart items, including fees and coupon applications.
   */
  const totalAmount: ComputedRef<number> = computed(
    () => types.value.reduce(
      (acc, type) => {
        const price = activeBatchPrice(type);
        const items: number[] = Array(type.quantidade).fill(price) as number[];

        for (const coupon of coupons.applied.value.get(type) ?? []) {
          for (let i = 0; i < coupon.uses; i++) {
            if (items.length < i) break;

            items[i] -= coupons.discount(items[i], coupon.coupon);
          }
        }

        // Apply fees after coupon discounts to ensure they are calculated even when there are no coupons
        return acc + items.reduce((acc, i) => acc + Fee.apply(i, events.selected!.convenience_fee, events.selected!.convenience_fee_mode), 0);
      },
      0,
    ),
  );

  async function fetchSectors(event: Event) {
    if (stepper.counter.value !== 1) {
      return;
    }

    loader.increment();

    sectors.value = [];

    try {
      const response = (await getTicketsBySector(event.id));

      // I know this is weird an probably should be filtered on the backend
      // In short, we need to take all sectors and order by it's order key
      // Inside them, we just return all sections with the `Section.tickets`
      // field filtered for only valid tickets.
      sectors.value = response.data.toSorted((a, b) => a.order - b.order).map(
        (sector: Sector) => ({
          ...sector,
          sections: sector.sections.map(
            (section: Section) => ({
              ...section,
              tickets: section.tickets.filter(
                t => t.current_batch != null || t.batches.length > 0,
              ).toSorted((a, b) => a.sorting_order - b.sorting_order),
            }),
          ),
        }),
      );
    } catch (error) {
      snackbar.error(error, "Falha ao carregar os setores e ingressos.");
    } finally {
      loader.decrement();
    }
  }

  function clear() {
    sectors.value.forEach((sector) => {
      sector.sections.forEach((section) => {
        section.tickets.forEach((ticket) => {
          ticket.quantidade = 0;
        });
      });
    });
  }

  function reset() {
    sectors.value = [];
    tickets.value = [];
    stepper.reset();
  }

  watch(() => events.selected, (event) => {
    if (event === null) {
      return;
    }

    fetchSectors(event);
  });

  return {
    steps,
    stepper,
    coupons,
    sectors,
    tickets,
    totalItems,
    totalFees,
    totalPrice,
    totalAmount,
    clear,
    reset,
  };
}, { persist: true });
